// Leaflet-based ortho viewer: pins/shapes with popovers, marking tools
// (point / polygon / measure), smooth zoom, fly-to focus, legend, compass.
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "./viewer.css";
import { useEffect, useRef, useState, type ReactNode } from "react";
import {
  clamp, fmtArea, fmtDist, geoArea, geoDist, isGeographic, latLngToPct, MEASURE_TIP, orthoBounds, pctToLatLng,
  setActiveOrtho, shapePts, type ActiveOrtho,
} from "./geo";
import {
  IcAreaM, IcCamera, IcCheck, IcChev, IcCube, IcCursor, IcEdit, IcImage, IcLayers, IcMaximize, IcMinus, IcMove, IcPin, IcPlus, IcPolygon,
  IcRuler, IcTrash, IcUndo, type DIconProps,
} from "./icons";
import { AreaTag, catOf, CategoryTag, ConfidenceMeter, sevMeta, SeverityPill, SourceTag } from "./meta";
import { getSources, SourceStrip } from "./sources";
import type { Defect, DefectShape, FocusState, MarkTool, SourceImage } from "./types";

// Leaflet's private fractional-zoom internals the smooth-zoom driver uses.
interface MapInternals {
  _move(center: L.LatLng, zoom: number, data?: { pinch: boolean }): void;
  _moveEnd(zoomChanged: boolean): void;
  _stop(): void;
  __faf?: number | null;
}
const internals = (map: L.Map) => map as unknown as L.Map & MapInternals;

// Fractional zoom (zoomSnap:0) leaves hairline sub-pixel seams between tiles
// (Leaflet issue #3575 / Chromium bug 600120). Growing each tile by 1px so
// neighbours overlap closes them — but ONLY together with viewer.css, which
// resets Leaflet 1.9's `mix-blend-mode: plus-lighter` on tiles back to normal:
// under additive blending the overlap itself renders as a bright white grid.
// Purely visual — tile positioning and coordinate mapping are unaffected.
const SeamlessTileLayer = L.TileLayer.extend({
  _initTile(this: L.TileLayer, tile: HTMLImageElement) {
    (L.TileLayer.prototype as unknown as { _initTile(t: HTMLImageElement): void })._initTile.call(this, tile);
    const size = this.getTileSize();
    tile.style.width = `${size.x + 1}px`;
    tile.style.height = `${size.y + 1}px`;
  },
});
const seamlessTileLayer = (url: string, opts: L.TileLayerOptions): L.TileLayer =>
  new (SeamlessTileLayer as unknown as new (u: string, o: L.TileLayerOptions) => L.TileLayer)(url, opts);

// Dark workbench grid drawn behind an ad hoc photo (no basemap on photos).
const GridLayer = L.GridLayer.extend({
  createTile(this: L.GridLayer) {
    const tile = L.DomUtil.create("canvas") as HTMLCanvasElement;
    const size = this.getTileSize();
    tile.width = size.x;
    tile.height = size.y;
    const ctx = tile.getContext("2d");
    if (ctx) {
      ctx.fillStyle = "#0b0f16";
      ctx.fillRect(0, 0, size.x, size.y);
      ctx.strokeStyle = "rgba(255,255,255,0.04)";
      ctx.lineWidth = 1;
      for (let x = 0; x <= size.x; x += 32) { ctx.beginPath(); ctx.moveTo(x + 0.5, 0); ctx.lineTo(x + 0.5, size.y); ctx.stroke(); }
      for (let y = 0; y <= size.y; y += 32) { ctx.beginPath(); ctx.moveTo(0, y + 0.5); ctx.lineTo(size.x, y + 0.5); ctx.stroke(); }
    }
    return tile;
  },
});
const gridLayer = (): L.GridLayer =>
  new (GridLayer as unknown as new (o?: L.GridLayerOptions) => L.GridLayer)({ zIndex: 1, noWrap: true });

// ── Smooth zoom ───────────────────────────────────────────────────────
// Drive fractional zoom with rAF through map._move({pinch}) rather than
// setView(): setView fires viewprereset, which makes tile layers tear down
// and rebuild each frame — a hard flicker with CDN tiles. _move({pinch})
// keeps existing tiles and scales them; _moveEnd loads sharp tiles once.
function zoomAroundSilent(map: L.Map, point: L.Point, zoom: number) {
  const scale = map.getZoomScale(zoom, map.getZoom());
  const viewHalf = map.getSize().divideBy(2);
  const offset = point.subtract(viewHalf).multiplyBy(1 - 1 / scale);
  internals(map)._move(map.containerPointToLatLng(viewHalf.add(offset)), zoom, { pinch: true });
}
export function flyToSmooth(map: L.Map, latlng: L.LatLng, targetZoom: number, duration: number) {
  const m = internals(map);
  if (m.__faf) cancelAnimationFrame(m.__faf);
  m._stop();
  const sZ = map.getZoom(), sC = map.getCenter();
  const tZ = Math.max(map.getMinZoom(), Math.min(map.getMaxZoom(), targetZoom));
  const start = performance.now();
  const ease = (t: number) => 1 - Math.pow(1 - t, 3);
  const stepFn = (now: number) => {
    const t = Math.min(1, (now - start) / duration), e = ease(t);
    m._move(L.latLng(sC.lat + (latlng.lat - sC.lat) * e, sC.lng + (latlng.lng - sC.lng) * e), sZ + (tZ - sZ) * e, { pinch: true });
    if (t < 1) { m.__faf = requestAnimationFrame(stepFn); }
    else { m.__faf = null; m._moveEnd(true); }
  };
  m.__faf = requestAnimationFrame(stepFn);
}
function smoothZoomBy(map: L.Map, delta: number) { flyToSmooth(map, map.getCenter(), map.getZoom() + delta, 240); }
function installSmoothWheel(map: L.Map) {
  let target = map.getZoom(), focus: L.Point | null = null, raf: number | null = null;
  const tick = () => {
    const cur = map.getZoom();
    let z: number;
    if (Math.abs(target - cur) < 0.008) { z = target; raf = null; }
    else { z = cur + (target - cur) * 0.3; raf = requestAnimationFrame(tick); }
    zoomAroundSilent(map, focus || map.getSize().divideBy(2), z);
    if (!raf) internals(map)._moveEnd(true); // settled → load sharp tiles once
  };
  const onWheel = (e: WheelEvent) => {
    e.preventDefault();
    internals(map)._stop();
    const settled = Math.abs(target - map.getZoom()) < 0.01;
    const baseT = settled ? map.getZoom() : target;
    const d = Math.max(-50, Math.min(50, e.deltaY));
    target = Math.max(map.getMinZoom(), Math.min(map.getMaxZoom(), baseT - d * 0.0042));
    focus = map.mouseEventToContainerPoint(e);
    if (!raf) raf = requestAnimationFrame(tick);
  };
  const el = map.getContainer();
  el.addEventListener("wheel", onWheel, { passive: false });
  return () => { el.removeEventListener("wheel", onWheel); if (raf) cancelAnimationFrame(raf); };
}

// ── Marking tools palette ─────────────────────────────────────────────
interface ToolDef { id: MarkTool; name: string; hint: string; Ic: (p: DIconProps) => ReactNode }
const MARK_TOOLS: ToolDef[] = [
  { id: "select", name: "Select & pan", hint: "Click a defect to open it · drag to pan the map", Ic: IcCursor },
  { id: "point", name: "Mark a point", hint: "Click the map to place a defect pin", Ic: IcPin },
  { id: "polygon", name: "Outline a defect", hint: "Click points around it · close on the first point", Ic: IcPolygon },
];
// Measure tools are ephemeral — nothing they draw is saved.
const MEASURE_TOOLS: ToolDef[] = [
  { id: "measure-line", name: "Measure distance", hint: "Click points · double-click to finish. Just a check, nothing is saved.", Ic: IcRuler },
  { id: "measure-area", name: "Measure an area", hint: "Click points · close on the first. Just a check, nothing is saved.", Ic: IcAreaM },
];
// measure=false hides the measure tools — ad hoc photos have no scale to
// measure against (no georeference).
export function ToolPalette({ tool, setTool, measure = true }: { tool: MarkTool; setTool: (t: MarkTool) => void; measure?: boolean }) {
  const [tip, setTip] = useState<{ name: string; hint: string; y: number } | null>(null);
  const enter = (t: ToolDef) => (e: React.MouseEvent<HTMLButtonElement>) =>
    setTip({ name: t.name, hint: t.hint, y: e.currentTarget.offsetTop + 19 });
  return (
    <div style={{ position: "relative" }}>
      <div style={{ display: "flex", flexDirection: "column", background: "rgba(14,18,25,0.86)", border: "1px solid var(--di-border)", borderRadius: 10, overflow: "hidden", backdropFilter: "blur(8px)" }}>
        {MARK_TOOLS.map((t, i) => <ToolBtn key={t.id} Ic={t.Ic} on={(tool || "select") === t.id} first={i === 0} onClick={() => setTool(t.id)} onEnter={enter(t)} onLeave={() => setTip(null)} />)}
        {measure && <div style={{ height: 3, background: "var(--di-border)" }}></div>}
        {measure && MEASURE_TOOLS.map((t, i) => <ToolBtn key={t.id} Ic={t.Ic} on={tool === t.id} first={i === 0} onClick={() => setTool(t.id)} onEnter={enter(t)} onLeave={() => setTip(null)} />)}
      </div>
      {tip &&
        <div style={{ position: "absolute", left: "calc(100% + 10px)", top: tip.y, transform: "translateY(-50%)", zIndex: 900, width: "max-content", maxWidth: 216, background: "var(--di-dark)", border: "1px solid var(--di-border)", borderRadius: 10, padding: "8px 11px", pointerEvents: "none", boxShadow: "0 10px 28px rgba(0,0,0,0.5)" }}>
          <div style={{ width: 8, height: 8, background: "var(--di-dark)", borderLeft: "1px solid var(--di-border)", borderBottom: "1px solid var(--di-border)", position: "absolute", left: -4.5, top: "50%", transform: "translateY(-50%) rotate(45deg)" }}></div>
          <div style={{ fontFamily: "var(--font-sans)", fontSize: 12.5, fontWeight: 600, color: "var(--di-text)", whiteSpace: "nowrap" }}>{tip.name}</div>
          {tip.hint && <div style={{ fontFamily: "var(--font-sans)", fontSize: 11.5, color: "var(--di-muted)", lineHeight: 1.45, marginTop: 3 }}>{tip.hint}</div>}
        </div>}
    </div>
  );
}
function ToolBtn({ Ic, on, first, onClick, onEnter, onLeave }: {
  Ic: (p: DIconProps) => ReactNode; on: boolean; first: boolean; onClick: () => void;
  onEnter: (e: React.MouseEvent<HTMLButtonElement>) => void; onLeave: () => void;
}) {
  const [h, setH] = useState(false);
  const col = on ? "var(--di-orange)" : h ? "var(--di-text)" : "var(--di-muted-2)";
  return (
    <button onClick={onClick}
      onMouseEnter={(e) => { setH(true); onEnter(e); }}
      onMouseLeave={() => { setH(false); onLeave(); }}
      style={{ width: 38, height: 38, border: "none", borderTop: first ? "none" : "1px solid var(--di-border)", background: on ? "rgba(249,115,22,0.14)" : h ? "rgba(255,255,255,0.05)" : "transparent", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", transition: "all 150ms ease" }}>
      <Ic size={16} color={col} />
    </button>
  );
}

// Floating readout chip used by the drawing / measure tools.
const measureLabelIcon = (text: string, opts: { centered?: boolean; final?: boolean; note?: string | null } = {}) => L.divIcon({
  className: "dd-marker",
  html: `<div style="transform:${opts.centered ? "translate(-50%,-50%)" : "translate(13px,-50%)"};display:inline-flex;align-items:baseline;background:rgba(11,15,22,${opts.final ? 0.95 : 0.85});border:1px solid ${opts.final ? "rgba(238,241,246,0.4)" : "var(--di-border)"};border-radius:6px;padding:3px 8px;font:700 11px var(--font-mono);color:#EEF1F6;white-space:nowrap;pointer-events:none">${text}${opts.note ? `<span class="dd-mnote" style="margin-left:6px;font-size:8px;letter-spacing:0.08em;text-transform:uppercase;color:#9AA4B2">${opts.note}<span class="dd-mnote-tip">${MEASURE_TIP}</span></span>` : ""}</div>`,
  iconSize: [0, 0], iconAnchor: [0, 0],
});

// Marker fill = severity colour (same coding as the report register and the
// severity pills); text flips dark on the luminous moderate amber.
const sevFill = (d: Defect) => sevMeta(d.severity).color;
const sevText = (d: Defect) => (sevMeta(d.severity).rank === 2 ? "#160d03" : "#fff");

function dotMarkupHTML(d: Defect, selected: boolean, dimmed: boolean, moving: boolean) {
  const ring = d.source === "user" ? "#6AA6FF" : d.modified ? "#F5B544" : null;
  const ringEl = ring ? `<div style="position:absolute;inset:-4px;border-radius:50%;border:2px ${d.source === "user" ? "solid" : "dashed"} ${ring}"></div>` : "";
  const selEl = selected && !moving ? '<div style="position:absolute;inset:-8px;border-radius:50%;border:2px solid rgba(255,255,255,0.9)"></div>' : "";
  const moveEl = moving ? '<div style="position:absolute;inset:-8px;border-radius:50%;border:2px dashed rgba(255,255,255,0.95)"></div>' : "";
  const badge = ring ? `<div style="position:absolute;top:-6px;right:-6px;width:14px;height:14px;border-radius:50%;background:${ring};border:1.5px solid #0b0f16;display:flex;align-items:center;justify-content:center;font:700 9px var(--font-mono);color:#0b0f16">${d.source === "user" ? "+" : "✎"}</div>` : "";
  return `<div style="position:relative;width:28px;height:28px;opacity:${dimmed ? 0.28 : 1};transform:scale(${selected ? 1.12 : 1});cursor:${moving ? "move" : "pointer"}">${ringEl}${selEl}${moveEl}<div style="width:28px;height:28px;border-radius:50%;background:${sevFill(d)};border:2px solid #160d03;display:flex;align-items:center;justify-content:center;box-shadow:0 2px 8px rgba(0,0,0,.55);font:700 12px var(--font-mono);color:${sevText(d)}">${d.ref}</div>${badge}</div>`;
}

// Square ref tag for outlined defects — anchored above the top-left of the
// shape's bounding box instead of a dot at the centroid.
function tagMarkupHTML(d: Defect, selected: boolean, dimmed: boolean, moving: boolean) {
  const ring = d.source === "user" ? "#6AA6FF" : d.modified ? "#F5B544" : null;
  const ringEl = ring ? `<div style="position:absolute;inset:-4px;border-radius:9px;border:2px ${d.source === "user" ? "solid" : "dashed"} ${ring}"></div>` : "";
  const selEl = selected && !moving ? '<div style="position:absolute;inset:-8px;border-radius:11px;border:2px solid rgba(255,255,255,0.9)"></div>' : "";
  const moveEl = moving ? '<div style="position:absolute;inset:-8px;border-radius:11px;border:2px dashed rgba(255,255,255,0.95)"></div>' : "";
  const badge = ring ? `<div style="position:absolute;top:-6px;right:-6px;width:14px;height:14px;border-radius:50%;background:${ring};border:1.5px solid #0b0f16;display:flex;align-items:center;justify-content:center;font:700 9px var(--font-mono);color:#0b0f16">${d.source === "user" ? "+" : "✎"}</div>` : "";
  return `<div style="position:relative;display:inline-flex;opacity:${dimmed ? 0.28 : 1};transform:scale(${selected ? 1.1 : 1});transform-origin:bottom left;cursor:${moving ? "move" : "pointer"}">${ringEl}${selEl}${moveEl}<div style="min-width:26px;height:22px;padding:0 7px;border-radius:6px;background:${sevFill(d)};border:2px solid #160d03;display:flex;align-items:center;justify-content:center;box-shadow:0 2px 8px rgba(0,0,0,.55);font:700 11.5px var(--font-mono);color:${sevText(d)}">${d.ref}</div>${badge}</div>`;
}

// ── Viewer ────────────────────────────────────────────────────────────
export interface ViewerProps {
  view: string;
  /** The real ortho pyramid to render (raster profile, CRS.Simple). */
  ortho: ActiveOrtho;
  defects: Defect[];
  selectedId: string | null;
  tool: MarkTool;
  movingId: string | null;
  filterSev: string;
  filterCat: string;
  focus?: FocusState;
  onAdd?: (x: number, y: number) => void;
  onAddShape?: (shape: DefectShape) => void;
  onExitTool?: () => void;
  onMove?: (id: string, x: number, y: number) => void;
  onMoveEnd?: () => void;
  onSelect: (id: string | null) => void;
  onEdit?: (d: Defect) => void;
  onReset?: (d: Defect) => void;
  onDelete?: (d: Defect) => void;
  onMoveToggle?: (id: string) => void;
  onViewSources?: (d: Defect, i?: number) => void;
  resolveSources?: (d: Defect) => SourceImage[];
  sourcesCalculating?: (d: Defect) => boolean;
  readOnly?: boolean;
  /** Hide every defect marker/shape + the popover (clean-ortho view). Tools,
      list and legend are unaffected. */
  hideDefects?: boolean;
  popoverExtra?: ((d: Defect) => ReactNode) | null;
  children?: ReactNode;
}

export function Viewer(props: ViewerProps) {
  const {
    ortho, defects, selectedId, tool, movingId, filterSev, filterCat, focus, hideDefects,
    onEdit, onReset, onDelete, onMoveToggle, onViewSources, resolveSources, sourcesCalculating, readOnly, popoverExtra, children,
  } = props;
  const elRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<L.Map | null>(null);
  const markersRef = useRef<Record<string, L.Marker>>({});
  const shapesRef = useRef<Record<string, L.Polygon>>({});
  const draggingRef = useRef<string | null>(null);
  const boundsRef = useRef<L.LatLngBounds | null>(null);
  const fitZoomRef = useRef<number | null>(null);
  const [pt, setPt] = useState<{ x: number; y: number; flip: boolean | null } | null>(null);
  const [zoomPct, setZoomPct] = useState(100);

  const visible = (d: Defect) =>
    (!filterSev || filterSev === "all" || d.severity === filterSev) && (!filterCat || filterCat === "all" || catOf(d) === filterCat);
  const cb = useRef<{
    tool: MarkTool;
    onAdd?: ViewerProps["onAdd"]; onAddShape?: ViewerProps["onAddShape"]; onExitTool?: ViewerProps["onExitTool"];
    onSelect: ViewerProps["onSelect"]; onMove?: ViewerProps["onMove"]; onMoveEnd?: ViewerProps["onMoveEnd"];
    selectedId: string | null; defects: Defect[]; visible: (d: Defect) => boolean; hideDefects: boolean;
  }>({ tool: "select", onSelect: () => {}, selectedId: null, defects: [], visible: () => true, hideDefects: false });
  cb.current = {
    tool, onAdd: props.onAdd, onAddShape: props.onAddShape, onExitTool: props.onExitTool,
    onSelect: props.onSelect, onMove: props.onMove, onMoveEnd: props.onMoveEnd,
    selectedId, defects, visible, hideDefects: !!hideDefects,
  };

  // Marker anchor: points at their coords; outlined defects hang their tag
  // off the top-left of the shape's bounding box.
  const shapeTagPct = (shape: DefectShape): [number, number] => {
    const pts = shapePts(shape);
    return [Math.min(...pts.map((p) => p[0])), Math.min(...pts.map((p) => p[1]))];
  };
  const markerLL = (d: Defect): L.LatLng => {
    if (!d.shape) return pctToLatLng(d);
    const [x, y] = shapeTagPct(d.shape);
    return pctToLatLng({ x, y });
  };
  const updatePopover = () => {
    const map = mapRef.current;
    if (!map || !(map as unknown as { _loaded?: boolean })._loaded) return;
    const c = cb.current;
    if (c.hideDefects) { setPt(null); return; }
    const d = c.defects.find((x) => x.id === c.selectedId);
    if (!d || !c.visible(d)) { setPt(null); return; }
    // Outlined defects: anchor to the top-center of the bounding box (or
    // bottom-center when there's no room above) so the popover never covers
    // the outline itself. Points anchor as before.
    if (d.shape) {
      const pts = shapePts(d.shape);
      const xs = pts.map((v) => v[0]), ys = pts.map((v) => v[1]);
      const cx = (Math.min(...xs) + Math.max(...xs)) / 2;
      const top = map.latLngToContainerPoint(pctToLatLng({ x: cx, y: Math.min(...ys) }));
      const flip = top.y < 220;
      const p = flip ? map.latLngToContainerPoint(pctToLatLng({ x: cx, y: Math.max(...ys) })) : top;
      setPt({ x: p.x, y: p.y, flip });
      return;
    }
    const p = map.latLngToContainerPoint(pctToLatLng(d));
    setPt({ x: p.x, y: p.y, flip: null });
  };

  // init map once per ortho (the workspace remounts the Viewer keyed by
  // ortho id, so the active-ortho registration happens before first render).
  useEffect(() => {
    const el = elRef.current;
    if (!el) return;
    setActiveOrtho(ortho);
    const bounds = orthoBounds();
    boundsRef.current = bounds;
    const geo = isGeographic(ortho);
    let map: L.Map;
    if (geo) {
      // Georeferenced ortho: standard Web Mercator map with the dark street
      // basemap (CARTO "Dark Matter") under the ortho pyramid — real
      // geographic context in the console's palette (design parity).
      const maxNative = ortho.maxZoom3857 ?? 22;
      const minNative = ortho.minZoom3857 ?? 12;
      const minZoom = Math.max(3, minNative - 4);
      const maxZoom = maxNative + 2;
      map = L.map(el, { zoomControl: false, attributionControl: false, minZoom, maxZoom, zoomSnap: 0, zoomDelta: 0.6, maxBoundsViscosity: 0.75, fadeAnimation: true, zoomAnimation: false, scrollWheelZoom: false, inertiaDeceleration: 2600 });
      // Attribution rendered as a custom overlay (MapAttribution) below.
      seamlessTileLayer("https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}.png", {
        subdomains: "abcd", minZoom, maxNativeZoom: 20, maxZoom, tileSize: 256, keepBuffer: 2, updateWhenZooming: true, zIndex: 1,
      }).addTo(map);
      // No crossOrigin: tiles load as plain <img>. bounds clips requests to
      // the footprint; minNativeZoom stops requests below the pyramid.
      seamlessTileLayer(ortho.tileUrl3857!, { minZoom, minNativeZoom: minNative, maxNativeZoom: maxNative, maxZoom, tms: false, bounds, tileSize: ortho.tileSize || 256, noWrap: true, keepBuffer: 1, updateWhenZooming: true, zIndex: 2 }).addTo(map);
      map.setView(bounds.getCenter(), Math.max(minZoom, maxNative - 6), { animate: false });
    } else if (ortho.photoSrc) {
      // Ad hoc photo: a flat CRS.Simple pixel plane. A dark workbench grid sits
      // behind the single image overlay; no tile pyramid, no georeference.
      map = L.map(el, { crs: L.CRS.Simple, zoomControl: false, attributionControl: false, minZoom: -5, maxZoom: 6, zoomSnap: 0, zoomDelta: 0.6, maxBoundsViscosity: 0.75, fadeAnimation: true, zoomAnimation: false, scrollWheelZoom: false, inertiaDeceleration: 2600 });
      gridLayer().addTo(map);
      L.imageOverlay(ortho.photoSrc, bounds, { zIndex: 2 }).addTo(map);
      map.setView(bounds.getCenter(), -2, { animate: false });
    } else {
      // gdal2tiles raster profile: pure pixel space, no CRS -> L.CRS.Simple.
      // One latlng unit = 2^maxZoom native pixels (geo.ts convention).
      map = L.map(el, { crs: L.CRS.Simple, zoomControl: false, attributionControl: false, minZoom: 0, maxZoom: ortho.maxZoom + 2, zoomSnap: 0, zoomDelta: 0.6, maxBoundsViscosity: 0.75, fadeAnimation: true, zoomAnimation: false, scrollWheelZoom: false, inertiaDeceleration: 2600 });
      // Ortho pyramid from the public bucket (assets.dig-int.ai; /r2 in dev);
      // bounds clips tile requests to the footprint; over-zoom past native.
      seamlessTileLayer(ortho.tileUrl, { minZoom: 0, maxNativeZoom: ortho.maxZoom, maxZoom: ortho.maxZoom + 2, tms: false, bounds, tileSize: ortho.tileSize || 256, noWrap: true, keepBuffer: 1, updateWhenZooming: true, zIndex: 2 }).addTo(map);
      map.setView(bounds.getCenter(), Math.max(0, ortho.maxZoom - 6), { animate: false });
    }
    mapRef.current = map;
    const detachWheel = installSmoothWheel(map);
    const refit = () => { map.invalidateSize(false); map.fitBounds(bounds, { animate: false, padding: [24, 24] }); map.setMaxBounds(bounds.pad(1.6)); fitZoomRef.current = map.getZoom(); setZoomPct(100); };
    requestAnimationFrame(refit);
    const ro = new ResizeObserver(() => map.invalidateSize(false));
    ro.observe(el);
    map.on("click", (e: L.LeafletMouseEvent) => {
      const c = cb.current;
      if (c.tool === "point") { const [x, y] = latLngToPct(e.latlng); c.onAdd && c.onAdd(x, y); }
      else if (!c.tool || c.tool === "select") c.onSelect(null);
      // polygon / measure clicks are handled by the drawing controller
    });
    map.on("move zoom viewreset", updatePopover);
    map.on("zoom zoomend", () => { const f = fitZoomRef.current; setZoomPct(Math.round(Math.pow(2, map.getZoom() - (f != null ? f : map.getZoom())) * 100)); });
    return () => { detachWheel(); ro.disconnect(); map.remove(); mapRef.current = null; markersRef.current = {}; shapesRef.current = {}; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // sync markers + shape layers
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    // Hide/Show defects: when hidden, drive both syncs with an empty list so
    // every existing marker/shape is removed and none are re-added.
    const list = hideDefects ? [] : defects;
    const have = markersRef.current;
    const ids = new Set(list.map((d) => d.id));
    Object.keys(have).forEach((id) => { if (!ids.has(id)) { map.removeLayer(have[id]!); delete have[id]; } });
    list.forEach((d) => {
      const selected = d.id === selectedId;
      const moving = movingId === d.id;
      const icon = d.shape
        ? L.divIcon({ html: tagMarkupHTML(d, selected, !visible(d), moving), className: "dd-marker" + (moving ? " dd-moving" : ""), iconSize: [46, 22], iconAnchor: [0, 28] })
        : L.divIcon({ html: dotMarkupHTML(d, selected, !visible(d), moving), className: "dd-marker" + (moving ? " dd-moving" : ""), iconSize: [28, 28], iconAnchor: [14, 14] });
      let m = have[d.id];
      if (!m) {
        m = L.marker(markerLL(d), { icon, draggable: false, autoPan: false, riseOnHover: true, keyboard: false });
        m.on("click", (e: L.LeafletMouseEvent) => {
          if (cb.current.tool && cb.current.tool !== "select") return; // don't steal clicks while drawing / measuring
          L.DomEvent.stopPropagation(e);
          cb.current.onSelect(cb.current.selectedId === d.id ? null : d.id);
        });
        m.on("dragstart", () => { draggingRef.current = d.id; });
        m.on("drag", (e: L.LeafletEvent) => {
          const [tx, ty] = latLngToPct((e.target as L.Marker).getLatLng());
          const cur = cb.current.defects.find((v) => v.id === d.id);
          // dragging the tag translates the whole shape: tag delta → anchor delta
          if (cur && cur.shape) { const [ax, ay] = shapeTagPct(cur.shape); cb.current.onMove && cb.current.onMove(d.id, cur.x + (tx - ax), cur.y + (ty - ay)); }
          else cb.current.onMove && cb.current.onMove(d.id, tx, ty);
        });
        m.on("dragend", () => { draggingRef.current = null; cb.current.onMoveEnd && cb.current.onMoveEnd(); });
        m.addTo(map);
        have[d.id] = m;
      } else if (draggingRef.current !== d.id) {
        m.setLatLng(markerLL(d));
        m.setIcon(icon);
      }
      // Pins stay locked until the user explicitly enters "move" mode.
      if (m.dragging) { if (moving) m.dragging.enable(); else m.dragging.disable(); }
    });
    const shapes = shapesRef.current;
    list.forEach((d) => {
      const existing = shapes[d.id];
      if (!d.shape) { if (existing) { map.removeLayer(existing); delete shapes[d.id]; } return; }
      const latlngs = shapePts(d.shape).map(([x, y]) => pctToLatLng({ x, y }));
      const selected = d.id === selectedId;
      const dim = !visible(d);
      const moving = movingId === d.id;
      // No fill tint: the interior stays transparent so the ortho's true
      // colour reads (fillOpacity 0 rather than fill:false keeps the
      // interior clickable). Outline alone carries selection/dim state.
      const sc = sevMeta(d.severity).color;
      const style: L.PathOptions = { color: sc, weight: selected ? 2.5 : 2, opacity: dim ? 0.14 : 0.9, fillColor: sc, fillOpacity: 0, dashArray: moving ? "6 7" : undefined };
      if (!existing) {
        const layer = L.polygon(latlngs, style);
        layer.on("click", (e: L.LeafletMouseEvent) => {
          if (cb.current.tool && cb.current.tool !== "select") return;
          L.DomEvent.stopPropagation(e);
          cb.current.onSelect(cb.current.selectedId === d.id ? null : d.id);
        });
        layer.addTo(map);
        shapes[d.id] = layer;
      } else { existing.setLatLngs(latlngs); existing.setStyle(style); }
    });
    Object.keys(shapes).forEach((id) => { if (!ids.has(id)) { map.removeLayer(shapes[id]!); delete shapes[id]; } });
    updatePopover();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defects, selectedId, filterSev, filterCat, movingId, hideDefects]);

  // fly to a defect when it's focused from the list
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !(map as unknown as { _loaded?: boolean })._loaded || !focus || focus.id == null) return;
    const d = cb.current.defects.find((x) => x.id === focus.id);
    if (!d) return;
    const base = fitZoomRef.current != null ? fitZoomRef.current : map.getZoom();
    const tz = Math.max(map.getZoom(), Math.min(map.getMaxZoom(), base + 2));
    flyToSmooth(map, pctToLatLng(d), tz, 480);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focus && focus.seq]);

  // ── Drawing controller (polygon click-to-outline · measure tools) ────
  const measureRef = useRef<L.LayerGroup[]>([]);
  useEffect(() => {
    if (tool === "measure-line" || tool === "measure-area") return;
    const map = mapRef.current;
    if (map) measureRef.current.forEach((g) => map.removeLayer(g));
    measureRef.current = [];
  }, [tool]);
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    const previewStyle: L.PathOptions = { color: "#F97316", weight: 2, dashArray: "6 6", fillColor: "#F97316", fillOpacity: 0.08 };
    if (tool === "polygon") {
      map.doubleClickZoom.disable();
      let pts: L.LatLng[] = [], fill: L.Polygon | null = null, nodes: L.CircleMarker[] = [], label: L.Marker | null = null;
      const sync = (cursor?: L.LatLng) => {
        const ll = cursor ? [...pts, cursor] : pts;
        if (!fill) fill = L.polygon(ll, previewStyle).addTo(map);
        else fill.setLatLngs(ll);
        nodes.forEach((n) => map.removeLayer(n));
        nodes = [];
        pts.forEach((p, i) => {
          const closeTarget = i === 0 && pts.length >= 3;
          nodes.push(L.circleMarker(p, { radius: closeTarget ? 6.5 : 4, color: closeTarget ? "#FFFFFF" : "#F97316", weight: 2, fillColor: "#160d03", fillOpacity: 1 }).addTo(map));
        });
        // live area readout beside the cursor once the outline encloses anything
        if (ll.length >= 3) {
          const at = cursor || pts[pts.length - 1]!;
          const icon = measureLabelIcon(fmtArea(geoArea(ll)));
          if (!label) label = L.marker(at, { icon, interactive: false }).addTo(map);
          else { label.setLatLng(at); label.setIcon(icon); }
        } else if (label) { map.removeLayer(label); label = null; }
      };
      const cleanup = () => { pts = []; if (fill) { map.removeLayer(fill); fill = null; } nodes.forEach((n) => map.removeLayer(n)); nodes = []; if (label) { map.removeLayer(label); label = null; } };
      const finish = () => {
        const out: L.LatLng[] = [];
        pts.forEach((p) => {
          const prev = out[out.length - 1];
          if (!prev || map.latLngToContainerPoint(prev).distanceTo(map.latLngToContainerPoint(p)) > 4) out.push(p);
        });
        if (out.length >= 3) cb.current.onAddShape && cb.current.onAddShape({ type: "polygon", points: out.map((p) => latLngToPct(p)) });
        cleanup();
      };
      const clickH = (e: L.LeafletMouseEvent) => {
        if (pts.length >= 3 && map.latLngToContainerPoint(pts[0]!).distanceTo(map.latLngToContainerPoint(e.latlng)) < 14) { finish(); return; }
        pts.push(e.latlng);
        sync();
      };
      const moveH = (e: L.LeafletMouseEvent) => { if (pts.length) sync(e.latlng); };
      const dbl = (e: L.LeafletMouseEvent) => { L.DomEvent.stop(e); finish(); };
      const key = (e: KeyboardEvent) => {
        if (e.key === "Escape") { if (pts.length) cleanup(); else cb.current.onExitTool && cb.current.onExitTool(); }
        else if (e.key === "Enter") finish();
      };
      map.on("click", clickH); map.on("mousemove", moveH); map.on("dblclick", dbl);
      window.addEventListener("keydown", key);
      return () => { map.doubleClickZoom.enable(); map.off("click", clickH); map.off("mousemove", moveH); map.off("dblclick", dbl); window.removeEventListener("keydown", key); cleanup(); };
    }
    if (tool === "measure-line" || tool === "measure-area") {
      const isArea = tool === "measure-area";
      map.doubleClickZoom.disable();
      const stroke: L.PathOptions = { color: "#EEF1F6", weight: 2, opacity: 0.92, dashArray: "5 6", interactive: false };
      const style: L.PathOptions = isArea ? { ...stroke, fillColor: "#EEF1F6", fillOpacity: 0.07 } : stroke;
      let pts: L.LatLng[] = [], layer: L.Polygon | L.Polyline | null = null, nodes: L.CircleMarker[] = [], label: L.Marker | null = null;
      const totalOf = (ll: L.LatLng[]): number | null => {
        let t = 0;
        for (let i = 1; i < ll.length; i++) { const d = geoDist(ll[i - 1]!, ll[i]!); if (d == null) return null; t += d; }
        return t;
      };
      const readout = (ll: L.LatLng[]) => (isArea ? (ll.length >= 3 ? fmtArea(geoArea(ll)) : null) : ll.length >= 2 ? fmtDist(totalOf(ll)) : null);
      const node = (p: L.LatLng, closeTarget: boolean) => L.circleMarker(p, { radius: closeTarget ? 6.5 : 3.5, color: "#EEF1F6", weight: 2, fillColor: "#0b0f16", fillOpacity: 1, interactive: false });
      const clearWork = () => { pts = []; if (layer) { map.removeLayer(layer); layer = null; } nodes.forEach((n) => map.removeLayer(n)); nodes = []; if (label) { map.removeLayer(label); label = null; } };
      const sync = (cursor?: L.LatLng) => {
        const ll = cursor ? [...pts, cursor] : pts;
        if (!layer) layer = (isArea ? L.polygon(ll, style) : L.polyline(ll, style)).addTo(map);
        else layer.setLatLngs(ll);
        nodes.forEach((n) => map.removeLayer(n));
        nodes = pts.map((p, i) => node(p, isArea && i === 0 && pts.length >= 3).addTo(map));
        const txt = readout(ll);
        const at = cursor || pts[pts.length - 1];
        if (txt && at) {
          const icon = measureLabelIcon(txt);
          if (!label) label = L.marker(at, { icon, interactive: false }).addTo(map);
          else { label.setLatLng(at); label.setIcon(icon); }
        } else if (label) { map.removeLayer(label); label = null; }
      };
      const finish = () => {
        const out: L.LatLng[] = [];
        pts.forEach((p) => {
          const prev = out[out.length - 1];
          if (!prev || map.latLngToContainerPoint(prev).distanceTo(map.latLngToContainerPoint(p)) > 4) out.push(p);
        });
        if (isArea ? out.length >= 3 : out.length >= 2) {
          const solid: L.PathOptions = { ...style, dashArray: undefined };
          const anchor = isArea
            ? L.latLng(out.reduce((a, p) => a + p.lat, 0) / out.length, out.reduce((a, p) => a + p.lng, 0) / out.length)
            : out[out.length - 1]!;
          const g = L.layerGroup([
            isArea ? L.polygon(out, solid) : L.polyline(out, solid),
            ...out.map((p) => node(p, false)),
            L.marker(anchor, { icon: measureLabelIcon(isArea ? fmtArea(geoArea(out)) : fmtDist(totalOf(out)), { final: true, centered: isArea, note: isArea ? "projected" : null }), interactive: false }),
          ]).addTo(map);
          measureRef.current.push(g);
        }
        clearWork();
      };
      const clickH = (e: L.LeafletMouseEvent) => {
        if (isArea && pts.length >= 3 && map.latLngToContainerPoint(pts[0]!).distanceTo(map.latLngToContainerPoint(e.latlng)) < 14) { finish(); return; }
        pts.push(e.latlng);
        sync();
      };
      const moveH = (e: L.LeafletMouseEvent) => { if (pts.length) sync(e.latlng); };
      const dbl = (e: L.LeafletMouseEvent) => { L.DomEvent.stop(e); finish(); };
      const key = (e: KeyboardEvent) => {
        if (e.key === "Escape") { if (pts.length) clearWork(); else cb.current.onExitTool && cb.current.onExitTool(); }
        else if (e.key === "Enter") finish();
      };
      map.on("click", clickH); map.on("mousemove", moveH); map.on("dblclick", dbl);
      window.addEventListener("keydown", key);
      return () => { map.doubleClickZoom.enable(); map.off("click", clickH); map.off("mousemove", moveH); map.off("dblclick", dbl); window.removeEventListener("keydown", key); clearWork(); };
    }
    if (tool === "point") {
      const key = (e: KeyboardEvent) => { if (e.key === "Escape") cb.current.onExitTool && cb.current.onExitTool(); };
      window.addEventListener("keydown", key);
      return () => window.removeEventListener("keydown", key);
    }
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tool]);

  // Toggle the crosshair class imperatively — React must not own className
  // on the Leaflet container, or re-renders wipe Leaflet's own classes.
  useEffect(() => { if (elRef.current) elRef.current.classList.toggle("dd-add", !!tool && tool !== "select"); }, [tool]);

  const sel = defects.find((d) => d.id === selectedId);
  const fit = () => { const map = mapRef.current; if (!map || !boundsRef.current) return; const z = map.getBoundsZoom(boundsRef.current, false, L.point(24, 24)); flyToSmooth(map, boundsRef.current.getCenter(), z, 440); };

  return (
    <div style={{ position: "absolute", inset: 0, background: "#0b0f16" }}>
      {/* Inline background beats Leaflet's light .leaflet-container default, so
          sub-pixel tile gaps at fractional zoom read dark instead of white. */}
      <div ref={elRef} style={{ position: "absolute", inset: 0, isolation: "isolate", background: "#0b0f16" }}></div>
      {pt && sel && !hideDefects && visible(sel) && <MapPopover d={sel} x={pt.x} y={pt.y} flip={pt.flip} moving={movingId === sel.id}
        onEdit={onEdit} onMove={() => onMoveToggle && onMoveToggle(sel.id)} onReset={onReset} onDelete={onDelete}
        onViewSources={onViewSources} resolveSources={resolveSources} sourcesCalculating={sourcesCalculating} readOnly={readOnly} popoverExtra={popoverExtra} />}

      <div style={{ position: "absolute", right: 14, bottom: 14, display: "flex", flexDirection: "column", background: "var(--di-surface)", border: "1px solid var(--di-border)", borderRadius: 10, overflow: "hidden", zIndex: 600 }}>
        <ZoomBtn Ic={IcPlus} onClick={() => mapRef.current && smoothZoomBy(mapRef.current, 0.8)} />
        <div style={{ height: 1, background: "var(--di-border)" }}></div>
        <ZoomBtn Ic={IcMinus} onClick={() => mapRef.current && smoothZoomBy(mapRef.current, -0.8)} />
        <div style={{ height: 1, background: "var(--di-border)" }}></div>
        <ZoomBtn Ic={IcMaximize} onClick={fit} />
      </div>
      <div style={{ position: "absolute", right: 14, bottom: 138, fontFamily: "var(--font-mono)", fontSize: 10, fontWeight: 700, color: "var(--di-muted)", background: "var(--di-surface)", border: "1px solid var(--di-border)", borderRadius: 6, padding: "3px 7px", zIndex: 600 }}>{zoomPct}%</div>
      {/* Compass + basemap attribution are geographic context; an ad hoc photo
          has no orientation or scale, so neither shows. */}
      {!ortho.photoSrc && <MapCompass />}
      {isGeographic(ortho) && <MapAttribution />}
      {children}
    </div>
  );
}

function ZoomBtn({ Ic, onClick }: { Ic: (p: DIconProps) => ReactNode; onClick: () => void }) {
  const [h, setH] = useState(false);
  return (
    <button onClick={(e) => { e.stopPropagation(); onClick(); }} onMouseEnter={() => setH(true)} onMouseLeave={() => setH(false)}
      style={{ width: 34, height: 34, border: "none", background: h ? "rgba(255,255,255,0.05)" : "transparent", color: h ? "var(--di-text)" : "var(--di-muted-2)", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }}>
      <Ic size={15} />
    </button>
  );
}

// ── Compass (static north indicator — the map is north-up) ───────────
export function MapCompass() {
  return (
    <div title="Map orientation: north up" style={{ position: "absolute", top: 66, left: 14, zIndex: 600, width: 46, height: 46, borderRadius: "50%", background: "rgba(14,18,25,0.86)", border: "1px solid var(--di-border)", backdropFilter: "blur(8px)", display: "flex", alignItems: "center", justifyContent: "center" }}>
      <svg width="34" height="34" viewBox="0 0 44 44" style={{ display: "block" }}>
        <text x="22" y="9.5" textAnchor="middle" style={{ fontFamily: "var(--font-mono)", fontSize: 8, fontWeight: 700, fill: "var(--di-muted-2)" }}>N</text>
        <path d="M22 12 L17.5 24 L22 21 L26.5 24 Z" style={{ fill: "var(--di-orange)" }} />
        <path d="M22 36 L17.5 24 L22 27 L26.5 24 Z" style={{ fill: "var(--di-subtle)" }} />
        <circle cx="22" cy="24" r="1.9" style={{ fill: "var(--di-text)" }} />
      </svg>
    </div>
  );
}

// ── Basemap attribution (OSM / CARTO) — required credit, kept subtle ──
export function MapAttribution() {
  const link: React.CSSProperties = { color: "var(--di-faint)", textDecoration: "none", pointerEvents: "auto" };
  return (
    <div style={{ position: "absolute", left: "50%", bottom: 5, transform: "translateX(-50%)", zIndex: 500, fontFamily: "var(--font-mono)", fontSize: 9, letterSpacing: "0.02em", color: "var(--di-faint)", pointerEvents: "none", whiteSpace: "nowrap" }}>
      © <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noopener noreferrer" style={link}>OpenStreetMap</a> · <a href="https://carto.com/attributions" target="_blank" rel="noopener noreferrer" style={link}>CARTO</a>
    </div>
  );
}

// ── Screen-space popover (anchored to a projected marker point) ───────
function MapPopover({ d, x, y, flip, moving, onEdit, onMove, onReset, onDelete, onViewSources, resolveSources, sourcesCalculating, readOnly, popoverExtra }: {
  d: Defect; x: number; y: number; flip: boolean | null; moving: boolean;
  onEdit?: (d: Defect) => void; onMove: (d: Defect) => void; onReset?: (d: Defect) => void; onDelete?: (d: Defect) => void;
  onViewSources?: (d: Defect, i?: number) => void; resolveSources?: (d: Defect) => SourceImage[];
  sourcesCalculating?: (d: Defect) => boolean; readOnly?: boolean; popoverExtra?: ((d: Defect) => ReactNode) | null;
}) {
  const flipped = flip != null ? flip : y < 220;
  const sources = (resolveSources || getSources)(d);
  const calc = !!(sourcesCalculating && sourcesCalculating(d));
  return (
    <div style={{ position: "absolute", left: x, top: y, zIndex: 500, pointerEvents: "none" }}>
      <div style={{ transform: flipped ? "translate(-50%, 22px)" : "translate(-50%, calc(-100% - 22px))", pointerEvents: "auto" }}>
        <div style={{ width: 274, background: "var(--di-dark)", border: "1px solid var(--di-border)", borderRadius: 13, boxShadow: "0 18px 44px rgba(0,0,0,0.6)", overflow: "hidden" }}>
          <div style={{ padding: "13px 14px 11px", display: "flex", flexDirection: "column", gap: 9 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span style={{ fontFamily: "var(--font-mono)", fontSize: 11, fontWeight: 700, color: "var(--di-orange)" }}>#{d.ref}</span>
              <span style={{ fontSize: 14, fontWeight: 600, color: "var(--di-text)", flex: 1, lineHeight: 1.2 }}>{d.title}</span>
              <SourceTag d={d} />
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}><SeverityPill sev={d.severity} /><CategoryTag cat={catOf(d)} />{d.shape && <AreaTag d={d} />}</div>
            {d.source === "ai" && d.confidence != null && <ConfidenceMeter value={d.confidence} />}
            {d.desc && <p style={{ fontSize: 12, color: "var(--di-muted)", lineHeight: 1.55 }}>{d.desc}</p>}
            <SourceStrip sources={sources} calculating={calc} readOnly={readOnly} onOpen={(i) => onViewSources && onViewSources(d, i)} />
          </div>
          {popoverExtra ? popoverExtra(d) : null}
          {!readOnly &&
            <div style={{ display: "flex", borderTop: "1px solid var(--di-border)" }}>
              <PopAction Ic={IcEdit} label="Edit" onClick={() => onEdit && onEdit(d)} />
              {d.shape
                ? <PopAction Ic={IcPolygon} label="Redraw" onClick={() => onMove(d)} />
                : <PopAction Ic={IcMove} label={moving ? "Drag pin" : "Move"} active={moving} onClick={() => onMove(d)} />}
              {(d.modified || d.source === "user") && <PopAction Ic={IcUndo} label="Reset" onClick={() => onReset && onReset(d)} />}
              <PopAction Ic={IcTrash} label="Delete" danger onClick={() => onDelete && onDelete(d)} />
            </div>}
        </div>
        <div style={{ width: 12, height: 12, background: "var(--di-dark)", position: "absolute", left: "50%", transform: "translateX(-50%) rotate(45deg)", [flipped ? "top" : "bottom"]: -6, borderRight: flipped ? "none" : "1px solid var(--di-border)", borderBottom: flipped ? "none" : "1px solid var(--di-border)", borderLeft: flipped ? "1px solid var(--di-border)" : "none", borderTop: flipped ? "1px solid var(--di-border)" : "none" }}></div>
      </div>
    </div>
  );
}
function PopAction({ Ic, label, onClick, danger, active }: {
  Ic: (p: DIconProps) => ReactNode; label: string; onClick: () => void; danger?: boolean; active?: boolean;
}) {
  const [h, setH] = useState(false);
  const col = active ? "var(--di-orange)" : danger ? (h ? "var(--di-destructive)" : "var(--di-subtle)") : h ? "var(--di-text)" : "var(--di-muted-2)";
  return (
    <button onClick={onClick} onMouseEnter={() => setH(true)} onMouseLeave={() => setH(false)}
      style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 4, padding: "9px 4px", border: "none", borderRight: "1px solid var(--di-border)", background: h ? "rgba(255,255,255,0.03)" : "transparent", color: col, cursor: "pointer", fontFamily: "var(--font-mono)", fontSize: 8.5, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em" }}>
      <Ic size={14} color={col} />{label}
    </button>
  );
}

// ── Legend ────────────────────────────────────────────────────────────
export function Legend({ floating }: { floating?: boolean }) {
  const items = [
    { ring: null as string | null, dashed: false, label: "AI detected" },
    { ring: "#F5B544", dashed: true, label: "Edited" },
    { ring: "#6AA6FF", dashed: false, label: "Manually Added" },
  ];
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 16, padding: floating ? "9px 13px" : 0, background: floating ? "rgba(14,18,25,0.86)" : "transparent", border: floating ? "1px solid var(--di-border)" : "none", borderRadius: 10, backdropFilter: floating ? "blur(8px)" : "none" }}>
      {items.map((it, i) => (
        <div key={i} style={{ display: "flex", alignItems: "center", gap: 7 }}>
          <div style={{ position: "relative", width: 16, height: 16, flexShrink: 0 }}>
            {it.ring && <div style={{ position: "absolute", inset: -2.5, borderRadius: "50%", border: `1.5px ${it.dashed ? "dashed" : "solid"} ${it.ring}` }}></div>}
            <div style={{ width: 16, height: 16, borderRadius: "50%", background: "var(--di-orange)", border: "1.5px solid #160d03" }}></div>
          </div>
          <span style={{ fontFamily: "var(--font-mono)", fontSize: 9.5, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: "var(--di-muted)" }}>{it.label}</span>
        </div>
      ))}
    </div>
  );
}

// ── View switcher ─────────────────────────────────────────────────────
export interface ViewDef { id: string; label: string; Ic: (p: DIconProps) => ReactNode; available: boolean }
export const VIEWS: ViewDef[] = [
  { id: "top", label: "Top", Ic: IcLayers, available: true },
  { id: "north", label: "North", Ic: IcImage, available: false },
  { id: "south", label: "South", Ic: IcImage, available: false },
  { id: "east", label: "East", Ic: IcImage, available: false },
  { id: "west", label: "West", Ic: IcImage, available: false },
  { id: "model", label: "3D", Ic: IcCube, available: true },
];
// canAdd: show the Photos entry even with zero photos, so the empty state is
// reachable to upload the first one (console). Read-only surfaces (client)
// leave it off, hiding the entry until a photo exists.
export interface PhotosSwitch { count: number; active: boolean; onClick: () => void; canAdd?: boolean }
const showPhotos = (p?: PhotosSwitch): p is PhotosSwitch => !!p && (p.count > 0 || !!p.canAdd);
export function ViewSwitcher({ view, setView, vertical, defectCounts, views, photos }: {
  view: string; setView: (v: string) => void; vertical?: boolean; defectCounts?: number; views?: ViewDef[]; photos?: PhotosSwitch;
}) {
  const all = views ?? VIEWS;
  if (vertical) {
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {all.filter((v) => v.available).map((v) => {
          const on = view === v.id;
          return (
            <button key={v.id} onClick={() => setView(v.id)}
              style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 12px", borderRadius: 11, border: `1px solid ${on ? "rgba(249,115,22,0.30)" : "var(--di-border)"}`, background: on ? "rgba(249,115,22,0.10)" : "var(--di-surface)", cursor: "pointer", textAlign: "left", transition: "all 150ms ease" }}>
              <div style={{ width: 30, height: 30, borderRadius: 8, background: on ? "rgba(249,115,22,0.14)" : "var(--di-ink)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}><v.Ic size={15} color={on ? "var(--di-orange)" : "var(--di-subtle)"} /></div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: on ? "var(--di-text)" : "var(--di-muted-2)" }}>{v.label}</div>
                <div style={{ fontFamily: "var(--font-mono)", fontSize: 8.5, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: "var(--di-subtle)", marginTop: 1 }}>{`${defectCounts} defects`}</div>
              </div>
            </button>
          );
        })}
        {showPhotos(photos) &&
          <button onClick={photos.onClick}
            style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 12px", borderRadius: 11, border: `1px solid ${photos.active ? "rgba(249,115,22,0.30)" : "var(--di-border)"}`, background: photos.active ? "rgba(249,115,22,0.10)" : "var(--di-surface)", cursor: "pointer", textAlign: "left", transition: "all 150ms ease" }}>
            <div style={{ width: 30, height: 30, borderRadius: 8, background: photos.active ? "rgba(249,115,22,0.14)" : "var(--di-ink)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}><IcCamera size={15} color={photos.active ? "var(--di-orange)" : "var(--di-subtle)"} /></div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: photos.active ? "var(--di-text)" : "var(--di-muted-2)" }}>Photos</div>
              <div style={{ fontFamily: "var(--font-mono)", fontSize: 8.5, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: "var(--di-subtle)", marginTop: 1 }}>{photos.count > 0 ? `${photos.count} ad hoc` : "Add photos"}</div>
            </div>
          </button>}
      </div>
    );
  }
  const model = all.find((v) => v.id === "model" && v.available);
  const onModel = !!model && view === model.id;
  const orthos = all.filter((v) => v.id !== "model");
  return (
    <div style={{ display: "flex", gap: 4, padding: 4, background: "var(--di-surface)", border: "1px solid var(--di-border)", borderRadius: 11 }}>
      <OrthoDropdown views={orthos} view={view} setView={setView} />
      {model &&
        <button onClick={() => setView(model.id)}
          style={{ display: "flex", alignItems: "center", gap: 7, padding: "7px 13px", borderRadius: 8, border: "none", background: onModel ? "var(--di-orange)" : "transparent", color: onModel ? "#160d03" : "var(--di-muted-2)", cursor: "pointer", fontFamily: "var(--font-sans)", fontSize: 13, fontWeight: 600, transition: "all 150ms ease" }}>
          <model.Ic size={14} color={onModel ? "#160d03" : "var(--di-subtle)"} />{model.label}
        </button>}
      {showPhotos(photos) && <PhotosViewBtn count={photos.count} active={photos.active} onClick={photos.onClick} />}
    </div>
  );
}

// Orthomosaic picker: the active ortho's name is the trigger; processed
// elevations sit in the menu (only ortho views, never the photos surface).
function OrthoDropdown({ views, view, setView }: { views: ViewDef[]; view: string; setView: (v: string) => void }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);
  const avail = views.filter((v) => v.available);
  if (!avail.length) return null;
  const cur = avail.find((v) => v.id === view);
  const on = !!cur;
  return (
    <div ref={ref} style={{ position: "relative" }}>
      <button onClick={() => setOpen((o) => !o)} title="Switch orthomosaic view"
        style={{ display: "flex", alignItems: "center", gap: 7, padding: "7px 10px 7px 13px", borderRadius: 8, border: "none", background: on ? "var(--di-orange)" : "transparent", color: on ? "#160d03" : "var(--di-muted-2)", cursor: "pointer", fontFamily: "var(--font-sans)", fontSize: 13, fontWeight: 600, transition: "all 150ms ease" }}>
        <IcLayers size={14} color={on ? "#160d03" : "var(--di-subtle)"} />{cur ? cur.label : "Orthos"}
        <IcChev size={12} color={on ? "#160d03" : "var(--di-subtle)"} style={{ transform: open ? "rotate(180deg)" : "none", transition: "transform 150ms ease" }} />
      </button>
      {open &&
        <div className="pop-in" style={{ position: "absolute", top: "calc(100% + 9px)", left: 0, zIndex: 900, width: 208, background: "var(--di-surface)", border: "1px solid var(--di-border)", borderRadius: 12, boxShadow: "0 18px 44px rgba(0,0,0,0.55)", padding: 5, display: "flex", flexDirection: "column", gap: 1 }}>
          <div style={{ padding: "8px 9px 4px", fontFamily: "var(--font-mono)", fontSize: 8.5, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.12em", color: "var(--di-subtle)" }}>Orthomosaics</div>
          {avail.map((v) => <OrthoDropRow key={v.id} v={v} on={v.id === view} onPick={() => { setView(v.id); setOpen(false); }} />)}
        </div>}
    </div>
  );
}
function OrthoDropRow({ v, on, onPick }: { v: ViewDef; on: boolean; onPick: () => void }) {
  const [h, setH] = useState(false);
  return (
    <button onClick={onPick} onMouseEnter={() => setH(true)} onMouseLeave={() => setH(false)} title={`View the ${v.label.toLowerCase()} ortho`}
      style={{ display: "flex", alignItems: "center", gap: 8, width: "100%", padding: "8px 9px", borderRadius: 8, border: "none", background: on ? "rgba(249,115,22,0.10)" : h ? "rgba(255,255,255,0.05)" : "transparent", color: on ? "var(--di-orange)" : "var(--di-text)", cursor: "pointer", fontFamily: "var(--font-sans)", fontSize: 13, fontWeight: 500, textAlign: "left", transition: "background 120ms ease" }}>
      <span style={{ flex: 1, minWidth: 0, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{v.label}</span>
      {on && <IcCheck size={11} color="var(--di-orange)" sw={3} />}
    </button>
  );
}
function PhotosViewBtn({ count, active, onClick }: { count: number; active: boolean; onClick: () => void }) {
  return (
    <>
      <div style={{ width: 1, alignSelf: "stretch", margin: "3px 2px", background: "var(--di-border)" }}></div>
      <button onClick={onClick} title="Ad hoc photos: handheld captures that fill gaps the orthomosaic and 3D model do not cover"
        style={{ display: "flex", alignItems: "center", gap: 7, padding: "7px 13px", borderRadius: 8, border: "none", background: active ? "var(--di-orange)" : "transparent", color: active ? "#160d03" : "var(--di-muted-2)", cursor: "pointer", fontFamily: "var(--font-sans)", fontSize: 13, fontWeight: 600, transition: "all 150ms ease" }}>
        <IcCamera size={14} color={active ? "#160d03" : "var(--di-subtle)"} />Photos
        {count > 0 && <span style={{ fontFamily: "var(--font-mono)", fontSize: 9.5, fontWeight: 700, lineHeight: 1.5, padding: "0 6px", borderRadius: 999, background: active ? "rgba(22,13,3,0.16)" : "var(--di-ink)", border: active ? "1px solid transparent" : "1px solid var(--di-border)", color: active ? "#160d03" : "var(--di-muted-2)" }}>{count}</span>}
      </button>
    </>
  );
}
